import {
  Directive,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';

@Directive({
  selector: '[appAdjustDropdownHeight]',
  standalone: true,
})
export class AdjustDropdownHeightDirective implements OnDestroy {
  private readonly select = inject(Select, { optional: true });
  private readonly multiSelect = inject(MultiSelect, { optional: true });
  private readonly ngZone = inject(NgZone);
  private pendingAdjustment?: ReturnType<typeof setTimeout>;

  private get component(): Select | MultiSelect {
    return (this.select ?? this.multiSelect)!;
  }

  constructor() {
    if (!this.select && !this.multiSelect) {
      throw new Error(
        'appAdjustDropdownHeight must be used on a p-select or p-multiselect component.',
      );
    }
  }

  @HostListener('onShow')
  @HostListener('onPanelShow')
  @HostListener('onFilter')
  adjustHeight(): void {
    // Runs outside the zone and after PrimeNG's calculateAutoSize() microtask; otherwise
    // the scroller's own height binding/auto-size would overwrite the value on the next CD.
    clearTimeout(this.pendingAdjustment);
    this.ngZone.runOutsideAngular(() => {
      this.pendingAdjustment = setTimeout(() => this.applyHeight(), 0);
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.pendingAdjustment);
  }

  private applyHeight(): void {
    const component = this.component;
    const itemSize = Number(component.virtualScrollItemSize);
    const scrollerElement: HTMLElement | undefined =
      component.scroller?.elementViewChild?.nativeElement;
    if (!scrollerElement || !itemSize) return;

    // visibleOptions includes group headers, which the scroller renders as rows too.
    const rowCount = Math.max(
      (component.visibleOptions() as unknown[] | null)?.length ?? 0,
      1,
    );
    scrollerElement.style.height = `min(${rowCount * itemSize}px, ${component.scrollHeight})`;

    component.overlayViewChild?.alignOverlay();
  }
}
